import MakeAnOfferModal from '@c/modals/MakeAnOfferModal';
import { Elements } from '@stripe/react-stripe-js';
import Button from '@ui/Button';
import { getCartItemFromProductDocument, isMobile } from '@util/index';
import { ProductDocument } from '@util/types/firestore/products';
import { UserDocument } from '@util/types/firestore/users';
import { useChat } from 'context/ChatContext';
import { useShoppingCart } from 'context/ShoppingCartContext';
import { useStripeContext } from 'context/StripeContext';
import { useToastContext } from 'context/ToastContext';
import { User } from 'firebase/auth';
import router from 'next/router';
import React from 'react';
import { useEffectivePrice } from 'context/StoreSaleContext';

interface Props {
  product: ProductDocument;
  seller: UserDocument | null;
  userDoc: UserDocument | null;
  user: User | null;
}
export const BuyAddOfferButtons = ({
  product,
  seller,
  user,
  userDoc,
}: Props) => {
  const { increaseCartQty, addGuestCartItem } = useShoppingCart();
  const salePrice = useEffectivePrice(product);
  const offerAvailable = product.offers_enabled && (!salePrice?.applied ||
    salePrice.unitCents / 100 > (product.minimum_offer_price ?? 0));
  const [openModal, setOpenModal] = React.useState('');
  const [isAdding, setIsAdding] = React.useState(false);

  // Disable the button while an add is in flight so rapid taps on a slow
  // network can't queue duplicate writes (GB #1323, 2026-07-15). increaseCartQty
  // owns the stock cap, the out-of-stock / over-max toast, and opening the
  // cart; addGuestCartItem does the same for guests. The context also holds an
  // in-flight lock, so this is belt-and-braces plus the visible pending state.
  const handleAddToCart = async () => {
    if (isAdding) return;
    const cartItem = getCartItemFromProductDocument(product, salePrice ?? undefined);
    if (!cartItem) return;
    setIsAdding(true);
    try {
      if (userDoc) {
        await increaseCartQty(cartItem);
      } else if (!user) {
        addGuestCartItem(cartItem, product.stock);
      }
    } finally {
      setIsAdding(false);
    }
  };
  const { setChatOpen } = useChat();
  const { stripePromise, error: stripeError } = useStripeContext();
  const { showErrorToast } = useToastContext();

  return (
    <>
      <div className="flex flex-col gap-[1.6rem]">
        <div className="flex flex-col gap-[1.6rem] xl:flex-row">
          {!!seller?.account_id && (
            <>
              <Button
                type="secondary"
                text={isAdding ? 'Adding…' : 'Add to Cart'}
                width="fluid"
                disabled={product.out_of_stock || isAdding}
                onClick={handleAddToCart}
              />
            </>
          )}
          {offerAvailable && (
            <Button
              type="tertiary"
              text="Make An Offer"
              width="fluid"
              onClick={() => {
                if (stripeError) {
                  showErrorToast('We couldn\'t load the offer form. Please disable ad blockers or try another browser.');
                  return;
                }
                if (userDoc) setOpenModal('offer');
                else if (!user) {
                  router.push({
                    pathname: '/login',
                    query: { redirect: router.asPath },
                  });
                }
              }}
            />
          )}
        </div>
      </div>
      {openModal === 'offer' && (
        <Elements stripe={stripePromise}>
          <MakeAnOfferModal
            isOpen={openModal === 'offer'}
            dismiss={(openDrawer) => {
              setOpenModal('');
              if (openDrawer) {
                if (isMobile()) {
                  router.push('/dashboard/offers');
                } else {
                  setChatOpen(true);
                }
              }
            }}
            product={product}
          />
        </Elements>
      )}
    </>
  );
};
